import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Marker, Popup, TileLayer, Polyline, useMapEvents } from "react-leaflet";
import L from "leaflet";
import axios from "axios";
import { Info, Phone, ExternalLink, Clock, Moon, ShieldCheck } from "lucide-react";
import { absUrl } from "./blocks/media";
import { DirectionsActions } from "./DirectionsActions";
import { nightStatus } from "../utils/openNow";

// Night map accents — the music player's palette (#120A24 ink · #54E3FF glow).
export const NIGHT_INK = "#120A24";
export const NIGHT_GLOW = "#54E3FF";
// Same pill as radio/HearIt.jsx (kept local: importing it would pull RadioContext → App into the home-page import cycle).
const PILL = "inline-flex items-center gap-1.5 rounded-full ring-1 ring-[#54E3FF]/60 px-3.5 py-2 text-xs sm:text-sm font-black tracking-wide transition-colors";
export const NIGHT_KIND_META = {
  music: { color: "#54E3FF", emoji: "🎵", label: "Live music & bars" },
  social: { color: "#C084FC", emoji: "🎤", label: "Karaoke · open mic · trivia · dancing" },
  food: { color: "#FBBF24", emoji: "🌮", label: "Late-night food" },
  sober: { color: "#4ADE80", emoji: "☕", label: "Sober-friendly" },
  safe: { color: "#FB7185", emoji: "🛟", label: "Safe-night" },
  ride: { color: "#FB7185", emoji: "🚕", label: "Safe ride" },
  pharmacy: { color: "#FB7185", emoji: "💊", label: "24h pharmacy" },
  er: { color: "#FB7185", emoji: "🏥", label: "Emergency room" },
  transit: { color: "#FB7185", emoji: "🚌", label: "Late transit" },
  cafe: { color: "#4ADE80", emoji: "☕", label: "All-night café" },
  crisis: { color: "#FB7185", emoji: "📞", label: "Crisis line" },
  other: { color: "#94A3B8", emoji: "📍", label: "Night spot" },
};
const meta = (k) => NIGHT_KIND_META[k] || NIGHT_KIND_META.other;

const cache = {};
// Same teardrops as the daytime map (white drop + logo, or category-colour drop + emoji, white edge) so the two maps read as one.
// Safe-night spots (red) are reference points, not destinations — a third the size.
const statusBadge = (st, size) => {
  if (!st) return "";
  const bg = st.status === "open" ? (st.confidence === "listing" ? "rgba(21,128,61,.72)" : "#15803d") : "rgba(75,85,99,.9)";
  return `<span data-testid="night-hours-badge" data-confidence="${st.confidence}" style="position:absolute;left:50%;top:${size + 2}px;transform:translateX(-50%);white-space:nowrap;font:700 9px/14px -apple-system,sans-serif;padding:0 5px;border-radius:7px;color:#fff;background:${bg};border:1px solid rgba(255,255,255,.35);box-shadow:0 1px 4px rgba(0,0,0,.5)">${st.short}</span>`;
};

export const nightIcon = (kind, logoUrl, count = 0, dim = false, st = null) => {
  const m = meta(kind);
  const key = `${kind}-${logoUrl || ""}-${count}-${dim ? "d" : ""}-${st ? `${st.confidence}:${st.short}` : ""}`;
  if (cache[key]) return cache[key];
  const small = m.color === NIGHT_KIND_META.safe.color && !logoUrl;
  const size = small ? 14 : logoUrl ? 46 : 38;
  const badge = count > 1 ? `<span style="position:absolute;top:-4px;right:-4px;background:${NIGHT_GLOW};color:${NIGHT_INK};font-size:11px;line-height:18px;height:18px;min-width:18px;padding:0 5px;border-radius:9px;font-weight:800;border:2px solid #fff;font-family:-apple-system,sans-serif">${count}</span>` : "";
  const drop = small
    ? `<div style="background:${m.color};width:14px;height:14px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:1.5px solid #fff;box-shadow:0 0 8px ${m.color}99,0 3px 10px rgba(0,0,0,.6)"></div>`
    : logoUrl
      ? `<div style="background:#fff;width:46px;height:46px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid rgba(255,255,255,.9);box-shadow:0 0 12px ${m.color}66,0 3px 10px rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center"><div style="transform:rotate(45deg);width:38px;height:38px;border-radius:50%;background:#fff;overflow:hidden;display:flex;align-items:center;justify-content:center"><img src="${absUrl(logoUrl)}" alt="" style="width:100%;height:100%;object-fit:cover;display:block" loading="lazy"/></div></div>`
      : `<div style="background:${m.color};width:38px;height:38px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 0 12px ${m.color}66,0 3px 10px rgba(0,0,0,.5)"><span style="transform:rotate(45deg);font-size:16px">${m.emoji}</span></div>`;
  cache[key] = L.divIcon({
    className: `night-marker${dim ? " night-marker-dim" : ""}`,
    html: `<div style="position:relative;width:${size}px;height:${size}px${dim ? ";opacity:.78" : ""}">${drop}${badge}${statusBadge(st, size)}</div>`,
    iconSize: [size, size], iconAnchor: [size / 2, size], popupAnchor: [0, -size],
  });
  return cache[key];
};

// Pins that would overlap on screen (< 52px apart at this zoom) get nudged 56px sideways, alternating
// west/east, with a dotted leader back to the true spot — same rule as the daytime map (Airport Tavern vs Real Art).
const nudge = (points, zoom) => {
  const mPerPx = (156543.03 * Math.cos((47.25 * Math.PI) / 180)) / Math.pow(2, zoom);
  const degPerPx = 360 / (256 * Math.pow(2, zoom));
  const list = points.map((p) => ({ ...p, pinLat: null, pinLng: null }));
  let flip = 0;
  for (let i = 0; i < list.length; i++) {
    for (let j = 0; j < i; j++) {
      const a = list[i], b = list[j];
      const dLat = (a.lat - (b.pinLat ?? b.lat)) * 111000;
      const dLng = (a.lng - (b.pinLng ?? b.lng)) * 75500;
      if (Math.hypot(dLat, dLng) / mPerPx < 52 && a.pinLat == null) {
        a.pinLat = a.lat;
        a.pinLng = a.lng + (flip++ % 2 === 0 ? -1 : 1) * 56 * degPerPx;
      }
    }
  }
  return list;
};

const NightZoom = ({ onZoom }) => {
  const map = useMapEvents({ zoomend: () => onZoom(map.getZoom()) });
  useEffect(() => { onZoom(map.getZoom()); }, [map, onZoom]);
  return null;
};

export const useNightData = (on, days) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!on) return;
    let dead = false;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/night`, { params: { days } }).then((r) => !dead && setData(r.data)).catch(() => !dead && setData({ venues: [], businesses: [], spots: [] }));
    return () => { dead = true; };
  }, [on, days]);
  return data;
};

// ⓘ hours — honest about what we know: admin-verified closing time, the business's own listing, or nothing (never dropped).
const HoursInfo = ({ closes, hours, verifiedAt, website, testId }) => {
  const [open, setOpen] = useState(false);
  const site = website ? (website.startsWith("http") ? website : `https://${website}`) : "";
  return (
    <div className="mt-1.5 text-[11px]" data-testid={testId}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 text-[#54E3FF] font-bold" data-testid={`${testId}-toggle`}>
        <Clock className="h-3 w-3" /> {closes ? `Open till ${closes}` : hours ? "Hours per their listing" : "Hours not posted"} <Info className="h-3 w-3 opacity-80" />
      </button>
      {open && (
        <div role="button" tabIndex={0} onClick={() => setOpen(false)} onKeyDown={(e) => { if (e.key === "Escape" || e.key === "Enter") setOpen(false); }} title="Tap anywhere to close" className="mt-1 cursor-pointer rounded-md bg-white/10 p-2 text-[#E5E7EB] leading-snug" data-testid={`${testId}-panel`}>
          {closes && <p className="m-0">{verifiedAt ? `Closing time verified ${new Date(verifiedAt).toLocaleDateString()} — best info we have.` : "Closing time from the venue — best info we have."}</p>}
          {hours && <p className="m-0 mt-0.5">Listed hours: {hours}</p>}
          {!closes && !hours && <p className="m-0">Many spots don't post hours, and we'd rather show them than miss them. Call ahead or check their page before heading out.</p>}
          {site && <a href={site} target="_blank" rel="noopener noreferrer" className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-[#54E3FF] px-2.5 py-1 font-bold !text-[#120A24] no-underline hover:bg-[#7eeaff]" data-testid={`${testId}-verify`}>Verify on their site <ExternalLink className="h-3 w-3" /></a>}
          <p className="m-0 mt-1.5 text-[10px] text-[#9CA3AF]">tap anywhere to close ✕</p>
        </div>
      )}
    </div>
  );
};

const StatusLine = ({ st, testId }) => st ? (
  <p className={`m-0 mt-1.5 text-[11px] font-bold ${st.status === "open" ? "text-[#86efac]" : "text-[#9CA3AF]"}`} data-testid={testId} data-confidence={st.confidence}>
    {st.status === "open" ? "● " : "○ "}{st.label}{st.confidence === "listing" ? " (per listing)" : st.confidence === "verified" ? " ✓" : ""}
    {st.note && <span className="block font-normal text-[#9CA3AF]">{st.note}</span>}
  </p>
) : null;

const PopupShell = ({ children }) => <div className="min-w-[220px] text-[#F3F4F6]" style={{ fontFamily: "inherit" }}>{children}</div>;

const Kinds = ({ kinds }) => kinds?.length ? (
  <div className="flex flex-wrap gap-1 mt-1">{kinds.map((k) => <span key={k} className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: `${meta(k).color}22`, color: meta(k).color }}>{meta(k).emoji} {meta(k).label}</span>)}</div>
) : null;

export const NightMarkers = ({ data, days }) => {
  const [zoom, setZoom] = useState(11);
  const placed = useMemo(() => {
    if (!data) return { venues: [], businesses: [], spots: [] };
    // Spots are tiny and never nudge venues; venues placed first so they keep their true spot when they win a tie.
    const all = nudge([...data.venues.map((v) => ({ ...v, _t: "v" })), ...data.businesses.map((b) => ({ ...b, _t: "b" })), ...data.spots.map((s) => ({ ...s, _t: "s" }))], zoom);
    return { venues: all.filter((p) => p._t === "v"), businesses: all.filter((p) => p._t === "b"), spots: all.filter((p) => p._t === "s") };
  }, [data, zoom]);
  if (!data) return null;
  const leaders = [...placed.venues, ...placed.businesses, ...placed.spots].filter((p) => p.pinLat != null);
  return (
    <>
      <NightZoom onZoom={setZoom} />
      {leaders.map((p, i) => <Polyline key={`nl-${i}`} positions={[[p.pinLat, p.pinLng], [p.lat, p.lng]]} pathOptions={{ color: NIGHT_GLOW, weight: 2, dashArray: "2 6", opacity: 0.9 }} />)}
      {/* Keyless dark basemap: standard OSM tiles inverted via CSS (.night-map .leaflet-tile-pane) — markers/popups live in other panes. */}
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
      {placed.venues.map((v) => { const st = nightStatus({ closes: v.closes, hours: v.hours, verifiedAt: v.hours_verified_at, events: v.events }); return (
        <Marker key={`nv-${v.key}`} position={[v.pinLat ?? v.lat, v.pinLng ?? v.lng]} icon={nightIcon(v.kinds[0] || "music", v.logo_url, v.events.length, false, st)} zIndexOffset={600}>
          <Popup maxWidth={320} minWidth={240} className="night-popup" autoPanPadding={[24, 72]}>
            <PopupShell>
              <div className="font-black text-[14px] leading-tight" data-testid={`night-venue-${v.key}`}>{v.venue_name}</div>
              {v.address && <div className="text-[11px] text-[#9CA3AF]">{v.address}{v.city ? `, ${v.city}` : ""}</div>}
              <Kinds kinds={v.kinds} />
              <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                {v.events.slice(0, 6).map((ev) => (
                  <Link key={ev.event_id} to={`/event/${ev.event_id}`} className="block rounded-md px-1.5 py-1 hover:bg-white/10 no-underline text-[#F3F4F6]" data-testid={`night-event-${ev.event_id}`}>
                    <div className="text-[12px] font-semibold truncate">{ev.title}</div>
                    <div className="text-[10px] text-[#54E3FF]">{days > 1 ? `${ev.day} · ` : ""}{ev.local_time}{ev.local_end ? `–${ev.local_end}` : ""}{ev.cover_charge ? ` · ${ev.cover_charge}` : ""}{ev.age_restriction ? ` · ${ev.age_restriction}` : ""}</div>
                  </Link>
                ))}
              </div>
              <StatusLine st={st} testId={`night-status-${v.key}`} />
              <HoursInfo closes={v.closes} hours={v.hours} verifiedAt={v.hours_verified_at} website={v.website} testId={`night-hours-${v.key}`} />
              <div className="mt-2 pt-2 border-t border-white/15"><DirectionsActions variant="compact" size="xs" lat={v.lat} lng={v.lng} address={v.address || ""} testId={`night-dir-${v.key}`} /></div>
            </PopupShell>
          </Popup>
        </Marker>
      ); })}
      {placed.businesses.map((b) => { const st = nightStatus({ closes: b.closes, hours: b.hours, verifiedAt: b.hours_verified_at }); return (
        <Marker key={`nb-${b.business_id || b.name}`} position={[b.pinLat ?? b.lat, b.pinLng ?? b.lng]} icon={nightIcon(b.kinds[0] || (b.sober_friendly ? "sober" : "other"), b.logo_url, 0, !!b.auto, st)} zIndexOffset={b.auto ? 400 : 500}>
          <Popup maxWidth={300} minWidth={220} className="night-popup" autoPanPadding={[24, 72]}>
            <PopupShell>
              <div className="font-black text-[14px] leading-tight" data-testid={`night-biz-${b.business_id || "cal"}`}>{b.name}</div>
              {b.address && <div className="text-[11px] text-[#9CA3AF]">{b.address}{b.city ? `, ${b.city}` : ""}</div>}
              <Kinds kinds={b.kinds} />
              {b.auto && !b.calendar_venue && <p className="m-0 mt-1 text-[11px] text-[#9CA3AF]" data-testid={`night-auto-${b.business_id}`}>No show listed tonight — music venues &amp; bars stay on the map as a precaution. Check before you go.</p>}
              {b.note && <p className="m-0 mt-1 text-[11px] text-[#E5E7EB]">{b.note}</p>}
              <StatusLine st={st} testId={`night-status-${b.business_id || "cal"}`} />
              <HoursInfo closes={b.closes} hours={b.hours} verifiedAt={b.hours_verified_at} website={b.website} testId={`night-hours-${b.business_id}`} />
              <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                {b.phone && <a href={`tel:${b.phone}`} className="inline-flex items-center gap-1 text-[#54E3FF] no-underline"><Phone className="h-3 w-3" /> {b.phone}</a>}
                {b.feature_page && <Link to={b.feature_page} className="text-[#54E3FF] underline">Their page on THE WALL →</Link>}
              </div>
              <div className="mt-2 pt-2 border-t border-white/15"><DirectionsActions variant="compact" size="xs" lat={b.lat} lng={b.lng} address={b.address || ""} testId={`night-dir-${b.business_id}`} /></div>
            </PopupShell>
          </Popup>
        </Marker>
      ); })}
      {placed.spots.map((s) => (
        <Marker key={`ns-${s.spot_id}`} position={[s.pinLat ?? s.lat, s.pinLng ?? s.lng]} icon={nightIcon(s.kind)} zIndexOffset={700}>
          <Popup maxWidth={280} minWidth={200} className="night-popup" autoPanPadding={[24, 72]}>
            <PopupShell>
              <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: meta(s.kind).color }}>{meta(s.kind).emoji} {meta(s.kind).label}</div>
              <div className="font-black text-[14px] leading-tight" data-testid={`night-spot-${s.spot_id}`}>{s.name}</div>
              {s.address && <div className="text-[11px] text-[#9CA3AF]">{s.address}</div>}
              {s.hours && <div className="text-[11px] text-[#54E3FF] mt-0.5"><Clock className="inline h-3 w-3" /> {s.hours}</div>}
              {s.note && <p className="m-0 mt-1 text-[11px] text-[#E5E7EB]">{s.note}</p>}
              <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                {s.phone && <a href={`tel:${s.phone}`} className="inline-flex items-center gap-1 text-[#54E3FF] no-underline"><Phone className="h-3 w-3" /> {s.phone}</a>}
                {s.url && <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#54E3FF] underline">Website <ExternalLink className="h-3 w-3" /></a>}
              </div>
              <div className="mt-2 pt-2 border-t border-white/15"><DirectionsActions variant="compact" size="xs" lat={s.lat} lng={s.lng} address={s.address || ""} testId={`night-dir-${s.spot_id}`} /></div>
            </PopupShell>
          </Popup>
        </Marker>
      ))}
    </>
  );
};

// Bottom-right card (where the Giving Map button sits by day): drink safety + the Hear-it clip + help lines.
export const BeforeYouGoOut = ({ spots = [] }) => {
  const [open, setOpen] = useState(false);
  const rides = spots.filter((s) => s.kind === "ride").slice(0, 3);
  return (
    <div className="absolute bottom-[6.5rem] sm:bottom-8 right-3 z-[500] max-w-[18rem]" data-testid="night-before-you-go">
      {open && (
        <div role="button" tabIndex={0} onClick={() => setOpen(false)} onKeyDown={(e) => { if (e.key === "Escape" || e.key === "Enter") setOpen(false); }} title="Tap anywhere to close" className="mb-2 cursor-pointer rounded-2xl p-3.5 text-[12px] leading-snug shadow-2xl ring-1 ring-[#54E3FF]/50" style={{ background: NIGHT_INK, color: "#E5E7EB" }} data-testid="night-before-you-go-card">
          <div className="font-black text-[#54E3FF] uppercase tracking-wider text-[10px] mb-1.5 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Before you go out <span className="ml-auto font-bold normal-case tracking-normal text-[#9CA3AF]">tap to close ✕</span></div>
          <ul className="m-0 pl-4 space-y-1">
            <li>Watch your drink being made; keep a hand over it. Set it down? Get a new one.</li>
            <li>Tell one person where you'll be and when you're home.</li>
            <li>Feeling way drunker than you drank? Say so — to staff, to a friend. <b>Call 911 if someone passes out.</b></li>
            <li>Rides: plan it before the first drink{rides.length ? ` — ${rides.map((r) => r.name).join(", ")}` : ""}.</li>
          </ul>
          <div className="mt-2 flex flex-wrap gap-1.5 items-center">
            <Link to="/safety" className="text-[#54E3FF] underline text-[11px]" data-testid="night-safety-link">Full guide →</Link>
          </div>
          <p className="m-0 mt-2 text-[10px] text-[#9CA3AF]">Crisis: call or text <b className="text-[#E5E7EB]">988</b> · Crystal Judson Family Justice Center 253-798-4166</p>
        </div>
      )}
      <button type="button" onClick={() => setOpen((o) => !o)} className={`${PILL} float-right shadow-lg ${open ? "bg-[#54E3FF] text-[#120A24] hover:bg-[#7eeaff]" : "bg-[#120A24] text-[#54E3FF] hover:bg-[#1b1333]"}`} data-testid="night-before-you-go-btn"><ShieldCheck className="h-3.5 w-3.5" /> {open ? "Close" : "Before you go out"}</button>
    </div>
  );
};

export const NightLifeButton = ({ active, onClick, className = "" }) => (
  <button type="button" onClick={onClick} aria-pressed={active} data-insight="Night Life map" data-testid="happening-map-night-filter"
    className={`inline-flex items-center gap-1.5 rounded-full ring-1 ring-[#54E3FF]/60 px-3.5 py-2 text-xs sm:text-sm font-black tracking-wide uppercase transition-colors ${active ? "bg-[#54E3FF] text-[#120A24] hover:bg-[#7eeaff]" : "bg-[#120A24] text-[#54E3FF] hover:bg-[#1b1333]"} ${className}`}>
    <Moon className="h-3.5 w-3.5" /> {active ? "Night Life ✓ · back to day" : "Night Life · open after 7pm"}
  </button>
);
